import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from '@wbk/config';
import useExploreQuery from '@/hooks/use-explore-query';
import Context from './context';
import type { ContentfulCategory, Zone } from '@wbk/contentful/api';

type Props = {
  children: React.ReactNode;
  categories: ContentfulCategory[] | undefined;
  zones: Zone[] | undefined;
};

const ExploreProvider = ({ children, categories, zones }: Props) => {
  const { t } = useTranslation('common');
  const { locale } = useRouter();
  const targetRef = useRef<HTMLElement | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<ContentfulCategory | undefined>(
    undefined
  );
  const [selectedZone, setSelectedZone] = useState<Zone | undefined>(undefined);

  const { categories: selectedCategories, zones: selectedZones, sortBy } = useExploreQuery({});

  const defaultSelectedCategory = (categories ?? [])?.find(
    (cat) => cat.slug === selectedCategories?.[0]
  );

  const defaultSelectedZone = (zones ?? []).find((x) => x.slug === selectedZones?.[0]);

  const sortOptions = useMemo(
    () => [
      { id: 'newest', value: 'newest', text: t('newest') },
      { id: 'oldest', value: 'oldest', text: t('oldest') },
    ],
    [t]
  );

  useEffect(() => {
    if (!selectedCategories?.[0]) {
      setSelectedCategory(undefined);
      return;
    }

    if (selectedCategory?.id === defaultSelectedCategory?.id) return;

    setSelectedCategory(defaultSelectedCategory || undefined);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want this to re-run when defaultSelectedCategory or selectedCategories changes
  }, [defaultSelectedCategory, selectedCategories, locale]);

  useEffect(() => {
    if (!selectedZones?.[0]) {
      setSelectedZone(undefined);
      return;
    }

    if (selectedZone?.id === defaultSelectedZone?.id) return;

    setSelectedZone(defaultSelectedZone || undefined);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want this to re-run when defaultSelectedZone or selectedZones changes
  }, [defaultSelectedZone, selectedZones, locale]);

  function getOptions(items: ContentfulCategory[] | Zone[] | undefined, defaultText: string) {
    return [
      { id: 'all', value: 'all', text: defaultText },
      ...(items?.map((item) => ({ id: item.slug, value: item.slug, text: item.title })) || []),
    ];
  }

  const categoryOptions = useMemo(
    () => getOptions(categories, t('all_categories')),
    [categories, t]
  );

  const zoneOptions = useMemo(() => getOptions(zones, t('all_zones')), [zones, t]);

  const value = useMemo(() => {
    return {
      targetRef: targetRef,
      categories,
      setSelectedCategory,
      selectedCategory,
      setSelectedZone,
      selectedZone,
      sortOptions,
      sortBy,
      zones,
      categoryOptions,
      zoneOptions,
    };
  }, [
    targetRef,
    categories,
    selectedCategory,
    selectedZone,
    zones,
    sortBy,
    categoryOptions,
    sortOptions,
    zoneOptions,
  ]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default ExploreProvider;
